import { Download, X } from 'lucide-react';
import styled, { keyframes } from 'styled-components';
import resumeEN from '../assets/resumes/Sabri_Belaidi_RESUME_EN.pdf';
import resumeFR from '../assets/resumes/Sabri_Belaidi_CV_FR.pdf';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideIn = keyframes`
  from { transform: translateY(-20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 99;
  display: flex;
  justify-content: center;
  align-items: center;
  animation: ${fadeIn} 0.3s ease-out;
`;

const ModalContentWrapper = styled.div`
  background-color: #000;
  padding: 2.5rem;
  border-radius: 0.7rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  min-width: 320px;
  color: white;
  animation: ${slideIn} 0.3s ease-out;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  padding: 0.5rem;
  transition: color 0.2s ease;

  &:hover {
    color: rgba(255, 255, 255, 1);
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const Title = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.6);
`;

const DownloadList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const DownloadItem = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 1rem 1.5rem;
  border-radius: 0.7rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: transparent;
  color: white;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.2);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0px);
  }
`;

const DownloadIcon = styled(Download)`
  transition: transform 0.2s ease;
  opacity: 0.8;
  
  ${DownloadItem}:hover & {
    transform: scale(1.1);
    opacity: 1;
  }
`;

const VersionWrapper = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
`;

const ResumeText = styled.span`
  font-size: 1rem;
  font-weight: 500;
`;

const LanguageLabel = styled.span`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.875rem;
  font-weight: 400;
`;

const availableLanguages = [
  { 
    language: 'English', 
    label: 'EN', 
    id: 'en',
    file: resumeEN,
    filename: 'sabri-belaidi-resume-en.pdf'
  },
  { 
    language: 'French', 
    label: 'FR', 
    id: 'fr',
    file: resumeFR,
    filename: 'sabri-belaidi-resume-fr.pdf'
  }
];

const Modal = ({ isOpen = true, onClose = () => {} }) => {
  if (!isOpen) return null;

  const handleDownload = async (fileUrl, filename) => {
    try {
      // Fetch the file
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      
      // Create a temporary URL for the blob
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary link element
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      
      // Append to document, click, and cleanup
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // Download failed
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContentWrapper onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>
          <X size={20} />
        </CloseButton>

        <Header>
          <Title>Download Resume</Title>
          <Subtitle>Select your preferred language version</Subtitle>
        </Header>
        
        <DownloadList>
          {availableLanguages.map(({ language, label, id, file, filename }) => (
            <DownloadItem 
              key={id} 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDownload(file, filename);
              }}
              title={`Download ${language} version`}
            >
              <VersionWrapper>
                <ResumeText>Resume</ResumeText>
                <LanguageLabel>{label}</LanguageLabel>
              </VersionWrapper>
              <DownloadIcon size={20} />
            </DownloadItem>
          ))}
        </DownloadList>
      </ModalContentWrapper>
    </ModalOverlay>
  );
};

export default Modal;